/**
 * Recursive BSP subdivision of a floor plate into rectangular cells that TILE
 * the area sharing 1-tile walls (no dead space). Cells are OUTER rects (inclusive,
 * including their bounding walls); a cell's floor is the rect shrunk one tile in.
 * Adjacent cells share their boundary wall column/row — so painting is: fill the
 * whole area Wall, then floor each cell's interior; the shared walls and the
 * exterior border are the un-floored gaps (all exactly 1 tile).
 *
 * Pure + deterministic (rule 4): the caller passes a seeded rng.
 */

import { CONFIG } from '../../config';

/** Axis-aligned inclusive rectangle. For a BSP cell this is the OUTER rect. */
export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Floor (interior) width of an outer rect — the rect minus its 1-tile wall ring. */
export function floorW(r: Rect): number {
  return r.x1 - r.x0 - 1;
}
export function floorH(r: Rect): number {
  return r.y1 - r.y0 - 1;
}

/** The floor (interior) rect of an outer cell — shrunk one tile in on all sides. */
export function cellFloor(r: Rect): Rect {
  return { x0: r.x0 + 1, y0: r.y0 + 1, x1: r.x1 - 1, y1: r.y1 - 1 };
}

function midJitter(lo: number, hi: number, jitter: number, rng: () => number): number {
  const mid = (lo + hi) / 2;
  return Math.round(mid + (rng() - 0.5) * 2 * jitter * (hi - lo));
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Subdivide `area` (an outer rect) into leaf cells. Splits the longer side; stops
 * when a cell can't be split (both floor dims < 2·minRoom+1) or, once it's within
 * `maxRoom`, on a small random chance (so rooms vary in size).
 *
 * `axis` restricts which cuts are allowed: 'y' only cuts horizontally (rooms
 * stack along y), 'x' only vertically. Used for the single-loaded bands beside a
 * corridor — splitting across a band's DEPTH would put a second room behind the
 * first, and that back room has no corridor wall to open onto.
 */
export function bspSubdivide(area: Rect, rng: () => number, axis: 'both' | 'x' | 'y' = 'both'): Rect[] {
  const { minRoom, maxRoom, splitJitter } = CONFIG.gen.bsp;
  // Outer width W splits into two cells each with floor ≥ minRoom (sharing one
  // wall) only when floorW = W-2 ≥ 2·minRoom+1, i.e. W ≥ 2·minRoom+3.
  const minSplit = 2 * minRoom + 3;
  const leaves: Rect[] = [];
  const stack: Rect[] = [area];
  let guard = 0;
  while (stack.length > 0 && guard++ < 8192) {
    const r = stack.pop()!;
    const w = r.x1 - r.x0 + 1;
    const h = r.y1 - r.y0 + 1;
    const canV = w >= minSplit && axis !== 'y';
    const canH = h >= minSplit && axis !== 'x';
    const oversize = (axis !== 'y' && floorW(r) > maxRoom) || (axis !== 'x' && floorH(r) > maxRoom);
    if ((!canV && !canH) || (!oversize && rng() < 0.14)) {
      leaves.push(r);
      continue;
    }
    // Split the longer FLOOR dimension when both are possible.
    const vertical = canV && canH ? floorW(r) >= floorH(r) : canV;
    if (vertical) {
      const lo = r.x0 + minRoom + 1;
      const hi = r.x1 - minRoom - 1;
      const p = clamp(midJitter(lo, hi, splitJitter, rng), lo, hi);
      stack.push({ x0: r.x0, y0: r.y0, x1: p, y1: r.y1 });
      stack.push({ x0: p, y0: r.y0, x1: r.x1, y1: r.y1 });
    } else {
      const lo = r.y0 + minRoom + 1;
      const hi = r.y1 - minRoom - 1;
      const p = clamp(midJitter(lo, hi, splitJitter, rng), lo, hi);
      stack.push({ x0: r.x0, y0: r.y0, x1: r.x1, y1: p });
      stack.push({ x0: r.x0, y0: p, x1: r.x1, y1: r.y1 });
    }
  }
  return leaves;
}

/** A doorway punched through the shared wall between two cells. */
export interface Adjacency {
  a: number; // leaf index
  b: number; // leaf index
  x: number; // door tile
  y: number;
}

/**
 * Wall-adjacency between two OUTER cells that share a boundary column/row long
 * enough for a door (≥ 3 overlapping tiles, so the door has wall on both sides).
 * Returns a door tile sitting on the shared wall between the two floors, or null.
 */
function shared(a: Rect, b: Rect): { x: number; y: number } | null {
  // Vertical shared wall (a and b are left/right neighbours sharing a column).
  const col = a.x1 === b.x0 ? a.x1 : b.x1 === a.x0 ? b.x1 : null;
  if (col !== null) {
    const oy0 = Math.max(a.y0, b.y0);
    const oy1 = Math.min(a.y1, b.y1);
    if (oy1 - oy0 >= 2) {
      return { x: col, y: Math.floor((oy0 + oy1) / 2) };
    }
    return null;
  }
  // Horizontal shared wall (top/bottom neighbours sharing a row).
  const row = a.y1 === b.y0 ? a.y1 : b.y1 === a.y0 ? b.y1 : null;
  if (row !== null) {
    const ox0 = Math.max(a.x0, b.x0);
    const ox1 = Math.min(a.x1, b.x1);
    if (ox1 - ox0 >= 2) {
      return { x: Math.floor((ox0 + ox1) / 2), y: row };
    }
  }
  return null;
}

/**
 * Every wall tile BETWEEN two adjacent cells' interiors — the full shared span,
 * not just the one door tile. Opening the whole run turns a shared wall into an
 * archway (used for corridor junctions, where a door would cut the route).
 */
export function sharedWallTiles(a: Rect, b: Rect): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  const col = a.x1 === b.x0 ? a.x1 : b.x1 === a.x0 ? b.x1 : null;
  if (col !== null) {
    const oy0 = Math.max(a.y0, b.y0);
    const oy1 = Math.min(a.y1, b.y1);
    for (let y = oy0 + 1; y <= oy1 - 1; y++) out.push({ x: col, y });
    return out;
  }
  const row = a.y1 === b.y0 ? a.y1 : b.y1 === a.y0 ? b.y1 : null;
  if (row !== null) {
    const ox0 = Math.max(a.x0, b.x0);
    const ox1 = Math.min(a.x1, b.x1);
    for (let x = ox0 + 1; x <= ox1 - 1; x++) out.push({ x, y: row });
  }
  return out;
}

/** All wall-adjacencies (with a door tile) between the leaf cells — O(n²). */
export function bspAdjacencies(leaves: Rect[]): Adjacency[] {
  const out: Adjacency[] = [];
  for (let i = 0; i < leaves.length; i++) {
    for (let j = i + 1; j < leaves.length; j++) {
      const d = shared(leaves[i]!, leaves[j]!);
      if (d) out.push({ a: i, b: j, x: d.x, y: d.y });
    }
  }
  return out;
}
